import * as React from "react";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "@/lib/utils";

/**
 * Badge — renders as the Aurora `.status-badge` chip.
 *
 * All variants resolve to Aurora color tokens via `src/index.css` chip
 * helper classes (`.status-neutral`, `.status-info`, `.status-success`,
 * `.status-warning`, `.status-error`, `.status-outline`). Do NOT introduce
 * hardcoded Tailwind colors (e.g. `bg-green-100`) on Badge instances —
 * pass a semantic variant instead.
 */
const badgeVariants = cva(
  "status-badge focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2",
  {
    variants: {
      variant: {
        // Brand-blue / "neutral filled" chip (Aurora bg-level-300 + tx-primary).
        // Maps to the existing `.status-booked` look used for order statuses.
        default: "status-neutral",
        secondary: "status-neutral",
        destructive: "status-error",
        outline: "status-outline",
        info: "status-info",
        success: "status-success",
        warning: "status-warning",
      },
    },
    defaultVariants: {
      variant: "default",
    },
  },
);

export interface BadgeProps extends React.HTMLAttributes<HTMLDivElement>, VariantProps<typeof badgeVariants> {}

function Badge({ className, variant, ...props }: BadgeProps) {
  return <div className={cn(badgeVariants({ variant }), className)} {...props} />;
}

export { Badge, badgeVariants };
